import { FC, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Stack, Typography, useTheme } from "@mui/material";
import type { IFamilyConfigCallBack, PortfolioData } from "types";
import { FILTERS_ENABLED } from "consts";
import { useFamilyState } from "./hooks/useFamilyState";
import { filterFamilies } from "./familyUtils";
import Header from "./components/Header";
import Search from "./components/Search";
import Filters from "./components/Filters";
import Card from "./components/Card";
import getStyles from "./styles";

type FamilyProps = {
  families: { data: PortfolioData | null };
  familyConfig: string[];
  familyConfigCallback: IFamilyConfigCallBack;
};

const Family: FC<FamilyProps> = ({ families, familyConfig, familyConfigCallback }) => {
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const { searchValue, selectedFamilyIds, familyFilters, colorArr, fluxArr, opticsArr, searchHandler, radioHandler, handleDelete, addFilter, isDisabled } =
    // @ts-ignore
    useFamilyState({ families, familyConfig, familyConfigCallback });

  // @ts-ignore
  const filteredFamilies = filterFamilies(families?.data?.families, searchValue, familyFilters, fluxArr);

  return (
    <Stack sx={styles.container}>
      <Header families={families} />

      <Search text={searchValue} onChange={searchHandler} />

      {FILTERS_ENABLED && (
        <Filters
          colorArr={colorArr}
          fluxArr={fluxArr}
          opticsArr={opticsArr}
          familyFilters={familyFilters}
          addFilter={addFilter}
          selectedFamilyIds={selectedFamilyIds}
          families={families?.data?.families}
          onDeleteChip={handleDelete}
        />
      )}

      <Box ref={scrollContainerRef} sx={styles.scrollable}>
        <Stack sx={styles.families}>
          {filteredFamilies.length > 0 ? (
            filteredFamilies.map((family, index) => (
              <Card
                key={family.id}
                family={family}
                index={index}
                isSelected={selectedFamilyIds.includes(family.id)}
                isDisabled={isDisabled(family.id)}
                onSelect={radioHandler}
                containerRef={scrollContainerRef}
              />
            ))
          ) : (
            <Box>
              {families?.data?.portfolio.id === null ? (
                <Alert variant="outlined" severity="error" icon={false}>
                  <Typography variant="body2">{t("Portfolio doesn't exist or has been deleted.")}</Typography>
                </Alert>
              ) : (
                <Typography variant="body2">{t("No results, please refine your search.")}</Typography>
              )}
            </Box>
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

export default Family;
